import { useState } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import PricingTable from '@/components/PricingTable';
import TestimonialSlider from '@/components/TestimonialSlider';
import ProcessSteps from '@/components/ProcessSteps';
import ContactFormDialog from '@/components/ContactFormDialog';
import { Shield, MessageSquare, FileText, Award } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export default function TrademarkOfficeActions() {
  const [showDialog, setShowDialog] = useState(false);

  const benefits = [
    { icon: Shield, title: 'Expert Defense', description: 'Professional legal responses to USPTO objections and refusals.' },
    { icon: MessageSquare, title: 'Clear Communication', description: 'Understand complex legal issues and our response strategy.' },
    { icon: FileText, title: 'Comprehensive Responses', description: 'Detailed, well-researched responses that address all concerns.' },
    { icon: Award, title: 'Higher Success Rate', description: 'Maximize your chances of overcoming office action rejections.' },
  ];

  const faqs = [
    { question: 'What is a trademark office action?', answer: 'An office action is an official letter from the USPTO examining attorney raising issues or objections with your trademark application that must be addressed before registration can proceed.' },
    { question: 'How long do I have to respond?', answer: 'You typically have 6 months to respond to an office action. Failure to respond within this timeframe will result in abandonment of your application.' },
    { question: 'What types of issues are raised in office actions?', answer: 'Common issues include likelihood of confusion with existing marks, descriptiveness, lack of distinctiveness, specimen issues, or procedural deficiencies.' },
    { question: 'Can all office actions be overcome?', answer: 'While not all office actions can be successfully overcome, professional legal representation significantly increases your chances of a favorable outcome.' },
  ];

  return (
    <div className="min-h-screen">
      <Navigation />
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1507679799987-c73779587ccf?crop=entropy&cs=srgb&fm=jpg&q=85)' }}>
          <div className="absolute inset-0 bg-gradient-to-r from-[#2C1810]/85 via-[#06214c]/75 to-[#A0522D]/70"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Playfair Display' }}>Trademark Office Actions Response</h1>
            <p className="text-lg text-white/90 max-w-2xl mx-auto mb-8">Expert support to overcome USPTO objections and move your trademark forward.</p>
            <button onClick={() => setShowDialog(true)} className="btn-primary">Get Started Today</button>
          </div>
        </div>
      </section>
      <section className="section section-abstract-bg">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>Why Choose Our Office Action Services?</h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">Professional legal response services to protect your trademark application.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="card text-center fade-in">
                  <div className="flex justify-center mb-4"><div className="w-16 h-16 bg-gradient-to-br from-[#06214c] to-[#D4A574] rounded-full flex items-center justify-center"><Icon className="text-white" size={32} /></div></div>
                  <h3 className="text-xl font-bold text-[#06214c] mb-3" style={{ fontFamily: 'Playfair Display' }}>{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <ProcessSteps />

      {/* Pricing Section */}
      <PricingTable />

      <section className="section section-abstract-shapes">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>Frequently Asked Questions</h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">Get answers about responding to trademark office actions.</p>
          </div>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-b border-[#D4A574]">
                  <AccordionTrigger className="text-left text-[#06214c] font-semibold hover:text-[#A0522D] py-4 text-[1.15rem]">{faq.question}</AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-4 text-[1.2rem]">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
      <TestimonialSlider autoSlide={true} />
      <section className="section bg-gradient-to-r from-[#06214c] to-[#A0522D] text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Playfair Display' }}>Need Help With an Office Action?</h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">Don't let an office action derail your trademark. Get expert help today.</p>
          <button onClick={() => setShowDialog(true)} className="bg-white text-[#06214c] px-8 py-3 rounded-full font-bold hover:bg-[#FFF5E6] transition-all transform hover:scale-105 shadow-xl">Get Started Today</button>
        </div>
      </section>
      <ContactFormDialog open={showDialog} onOpenChange={setShowDialog} source="office_actions" />
      <Footer />
    </div>
  );
}